import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import {
  EditorView,
  keymap,
  drawSelection,
  highlightActiveLineGutter,
  lineNumbers as lineNumbersExt,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import {
  searchKeymap,
  highlightSelectionMatches,
  search,
  openSearchPanel,
} from '@codemirror/search';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { autocompletion, completionKeymap } from '@codemirror/autocomplete';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { vim } from '@replit/codemirror-vim';
import { markbirdEditorTheme } from '../editor/theme';
import { applyFormat, type FormatAction } from '../editor/format';
import { runTableCommand, tableKeymap, type TableCommand } from '../editor/table';
import { slashCompletions } from '../editor/snippets';
import { wikiCompletions } from '../editor/wikicomplete';
import { useT } from '../i18n/useT';

export interface EditorHandle {
  /** 1-based source line currently at the top of the viewport. */
  getTopLine: () => number;
  /** Scroll so `line` (1-based) sits at the top of the viewport. */
  scrollToLine: (line: number) => void;
  /** Current selection as absolute offsets. */
  getSelection: () => { anchor: number; head: number };
  setSelection: (anchor: number, head: number) => void;
  getScrollTop: () => number;
  setScrollTop: (top: number) => void;
  /** Apply a Markdown formatting action to the current selection. */
  format: (action: FormatAction) => void;
  /** Run a table command; false when the cursor is not in a table. */
  table: (command: TableCommand) => boolean;
  /** Open the find & replace panel. */
  openSearch: () => void;
  focus: () => void;
}

interface EditorProps {
  /** Document identity — changing it replaces the whole document. */
  docId: string;
  value: string;
  vimMode: boolean;
  lineNumbers: boolean;
  fontSize: number;
  typewriter: boolean;
  onChange: (value: string) => void;
  onScroll?: () => void;
  onCursorActivity?: () => void;
}

const vimCompartment = new Compartment();
const lineNumbersCompartment = new Compartment();
const typewriterCompartment = new Compartment();

/** Typewriter scrolling: keep the caret line vertically centered. */
function typewriterExtension(): Extension {
  return EditorView.updateListener.of((update) => {
    if (!update.selectionSet && !update.docChanged) return;
    const view = update.view;
    // Defer the scroll dispatch out of the update cycle.
    requestAnimationFrame(() => {
      const head = view.state.selection.main.head;
      view.dispatch({ effects: EditorView.scrollIntoView(head, { y: 'center' }) });
    });
  });
}

/** Store dropped/pasted image files and insert `![alt](mbimg://id)` at cursor. */
async function insertImageFiles(view: EditorView, files: File[]): Promise<void> {
  const imgs = files.filter((f) => f.type.startsWith('image/'));
  if (!imgs.length) return;
  const { putImage } = await import('../lib/images');
  const parts: string[] = [];
  for (const f of imgs) {
    const ref = await putImage(f);
    const alt = (f.name || 'image').replace(/\.[^./\\]+$/, '') || 'image';
    parts.push(`![${alt}](${ref})`);
  }
  const text = parts.join('\n');
  const sel = view.state.selection.main;
  view.dispatch({
    changes: { from: sel.from, to: sel.to, insert: text },
    selection: { anchor: sel.from + text.length },
  });
  view.focus();
}

/** paste/drop handlers that intercept image files (other content falls through). */
const imageDropExtension = EditorView.domEventHandlers({
  paste(event, view) {
    const files = event.clipboardData?.files;
    const imgs = files ? [...files].filter((f) => f.type.startsWith('image/')) : [];
    if (!imgs.length) return false;
    event.preventDefault();
    void insertImageFiles(view, imgs);
    return true;
  },
  drop(event, view) {
    const files = event.dataTransfer?.files;
    const imgs = files ? [...files].filter((f) => f.type.startsWith('image/')) : [];
    if (!imgs.length) return false;
    event.preventDefault();
    event.stopPropagation(); // beat the page-level markdown dropzone
    void insertImageFiles(view, imgs);
    return true;
  },
});

export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor(
  {
    docId,
    value,
    vimMode,
    lineNumbers,
    fontSize,
    typewriter,
    onChange,
    onScroll,
    onCursorActivity,
  },
  ref,
) {
  const t = useT();
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  // Keep the latest callbacks without re-creating the editor.
  const cbRef = useRef({ onChange, onScroll, onCursorActivity });
  cbRef.current = { onChange, onScroll, onCursorActivity };

  useImperativeHandle(ref, (): EditorHandle => ({
    getTopLine: () => {
      const view = viewRef.current;
      if (!view) return 1;
      const block = view.lineBlockAtHeight(view.scrollDOM.scrollTop);
      return view.state.doc.lineAt(block.from).number;
    },
    scrollToLine: (line) => {
      const view = viewRef.current;
      if (!view) return;
      const clamped = Math.max(1, Math.min(line, view.state.doc.lines));
      const pos = view.state.doc.line(clamped).from;
      view.dispatch({ effects: EditorView.scrollIntoView(pos, { y: 'start' }) });
    },
    getSelection: () => {
      const sel = viewRef.current?.state.selection.main;
      return { anchor: sel?.anchor ?? 0, head: sel?.head ?? 0 };
    },
    setSelection: (anchor, head) => {
      const view = viewRef.current;
      if (!view) return;
      const len = view.state.doc.length;
      view.dispatch({
        selection: { anchor: Math.min(anchor, len), head: Math.min(head, len) },
      });
    },
    getScrollTop: () => viewRef.current?.scrollDOM.scrollTop ?? 0,
    setScrollTop: (top) => {
      if (viewRef.current) viewRef.current.scrollDOM.scrollTop = top;
    },
    format: (action) => {
      if (viewRef.current) applyFormat(viewRef.current, action);
    },
    table: (command) => (viewRef.current ? runTableCommand(viewRef.current, command) : false),
    openSearch: () => {
      const view = viewRef.current;
      if (view) {
        view.focus();
        openSearchPanel(view);
      }
    },
    focus: () => viewRef.current?.focus(),
  }));

  // Create the editor once per document identity.
  useEffect(() => {
    if (!hostRef.current) return;

    const baseExtensions: Extension = [
      // Vim FIRST. Its keymap has to see a key before any other keymap does —
      // the plugin's own README says so. It used to sit near the end, where the
      // default keymap's Escape binding took the key first: Esc never returned
      // to normal mode, so Vim mode could enter insert mode and never leave it.
      vimCompartment.of(vimMode ? vim() : []),
      history(),
      drawSelection(),
      highlightActiveLineGutter(),
      indentOnInput(),
      bracketMatching(),
      highlightSelectionMatches(),
      EditorView.lineWrapping,
      // Editor-scoped Markdown formatting shortcuts (only fire when focused, so
      // they don't collide with the global palette/sidebar bindings).
      keymap.of([
        {
          key: 'Mod-b',
          run: (v) => {
            applyFormat(v, 'bold');
            return true;
          },
        },
        {
          key: 'Mod-i',
          run: (v) => {
            applyFormat(v, 'italic');
            return true;
          },
        },
        {
          key: 'Mod-e',
          run: (v) => {
            applyFormat(v, 'code');
            return true;
          },
        },
      ]),
      // Completion keymap FIRST (higher precedence) so Enter/Tab accept a snippet
      // when the menu is open instead of inserting a newline.
      keymap.of(completionKeymap),
      // Ahead of indentWithTab: inside a table Tab moves between cells.
      keymap.of(tableKeymap),
      keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap, indentWithTab]),
      markdown({ base: markdownLanguage, codeLanguages: languages }),
      // Find & replace panel (Cmd/Ctrl+F) and the slash snippet menu.
      search({ top: true }),
      autocompletion({
        override: [slashCompletions, wikiCompletions],
        icons: false,
        defaultKeymap: false,
      }),
      lineNumbersCompartment.of(lineNumbers ? lineNumbersExt() : []),
      typewriterCompartment.of(typewriter ? typewriterExtension() : []),
      imageDropExtension,
      markbirdEditorTheme,
      // CodeMirror's content element is a textbox with no name of its own. The
      // explicit tabindex changes nothing for keyboard users — a contenteditable
      // is already in the tab order — but it makes that focusability visible to
      // tools that only count tabindex, so the scroll container around it is not
      // reported as unreachable.
      EditorView.contentAttributes.of({ 'aria-label': t('editor.aria'), tabindex: '0' }),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          cbRef.current.onChange(update.state.doc.toString());
        }
        if (update.selectionSet) {
          cbRef.current.onCursorActivity?.();
        }
      }),
    ];

    const view = new EditorView({
      state: EditorState.create({ doc: value, extensions: baseExtensions }),
      parent: hostRef.current,
    });
    viewRef.current = view;

    const scrollHandler = () => cbRef.current.onScroll?.();
    view.scrollDOM.addEventListener('scroll', scrollHandler, { passive: true });

    return () => {
      view.scrollDOM.removeEventListener('scroll', scrollHandler);
      view.destroy();
      viewRef.current = null;
    };
    // Recreate only when the document identity changes. `value`/`vimMode`
    // updates are reconciled by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // Reconcile external value changes (e.g. loading a doc) without losing focus.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
    }
  }, [value]);

  // Toggle Vim mode via compartment reconfiguration.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: vimCompartment.reconfigure(vimMode ? vim() : []),
    });
  }, [vimMode]);

  // Toggle line numbers via compartment reconfiguration.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: lineNumbersCompartment.reconfigure(lineNumbers ? lineNumbersExt() : []),
    });
  }, [lineNumbers]);

  // Toggle typewriter scrolling via compartment reconfiguration.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: typewriterCompartment.reconfigure(typewriter ? typewriterExtension() : []),
    });
  }, [typewriter]);

  return (
    <div
      ref={hostRef}
      className="mb-editor-host"
      style={{ ['--mb-editor-font-size' as string]: `${fontSize}px` }}
    />
  );
});
